import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useAppServices, useUploadImage } from "../../../../hook/services";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { useKnowledgebaseCustomizationInfo } from "../../../../context/knowledgebaseCustomization";
import ThemedLoader from "../../../../components/ThemedLoader";

const KnowledgebaseCustomization = () => {
  const AppService = useAppServices();
  const [
    KnowledgebaseCustomization,
    languages,
    Update,
    getKnowledgebaseCustomization,
  ] = useKnowledgebaseCustomizationInfo();
  const navigate = useNavigate();
  const uploadImage = useUploadImage();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [imageChange, setImageChange] = useState(false);
  const [domainUpdate, setDomainUpdate] = useState(false);
  const [formValues, setFormValues] = useState(KnowledgebaseCustomization);
  const [locations, setLocations] = useState([]);

  const getLocations = async () => {
    try {
      const { response } =
        await AppService.sidenav_customizer_custom_level.getLocations({
          query: `company_id=${companyId}`,
        });
      if (response && Array.isArray(response.accounts)) {
        const mappedLocations = response.accounts.map((loc) => ({
          value: loc.id,
          label: loc.name,
        }));
        setLocations(mappedLocations);
      } else {
        setLocations([]);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLocations();
  }, []);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormValues((prev) => ({ ...prev, logo: file }));
      setImageChange(true);
    }
  };

  const handleSelectChange = (field, selectedOption) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: selectedOption.value,
    }));
  };

  const handleMultiSelectChange = (field, selectedOptions) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: selectedOptions,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let logoResponse = formValues.logo;
      if (imageChange && formValues.logo instanceof File) {
        const image_response = await uploadImage({
          file: formValues.logo,
          desiredPath: `customizer/knowledgebase/logo`,
        });
        if (image_response.response) {
          logoResponse = image_response.response.data;
        }
      }

      const allowLanguagesIds = formValues.allowMultipleLanguages
        ? formValues.allowLanguages
        : [];

      const defaultLanguageId = formValues.languageId;

      const { response } = await AppService.knowledgebase_customization.create({
        payload: {
          ...formValues,
          logo: logoResponse,
          domainUpdate: domainUpdate,
          companyId: companyId,
          allowLanguages: allowLanguagesIds,
          languageId: defaultLanguageId,
        },
      });

      if (response) {
        toast.success("Customization saved successfully.");
        Update(response.data);
        setDomainUpdate(false);
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    }
  };

  const configureCustomMenuLink = async (e) => {
    e.preventDefault();
    try {
      const { response } =
        await AppService.knowledgebase_customization.create_custom_menu_link({
          payload: { ...formValues, companyId: companyId },
        });

      if (response) {
        toast.success("Configuration saved successfully.");
        getKnowledgebaseCustomization();
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    }
  };

  const ColorPicker = ({ label, field }) => (
    <div className="kc-field">
      <label className="kc-label">{label}</label>
      <div className="d-flex gap-2 align-items-center">
        <div className="kc-color-swatch position-relative">
          <input
            type="color"
            value={formValues[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            className="kc-color-input"
            aria-label={`${label} color`}
          />
          <span
            className="kc-color-preview"
            style={{ background: formValues[field] }}
            aria-hidden
          />
        </div>
        <input
          type="text"
          className="kc-input"
          value={formValues[field]}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={formValues[field]}
        />
      </div>
    </div>
  );

  if (loading) {
    return <ThemedLoader variant="page" label="Loading customization" />;
  }

  return (
    <div onSubmit={handleSubmit} className="kc-page">
      <div className="container-fluid py-4 px-4">
        {/* Back */}
        <button
          className="kc-back-btn d-inline-flex align-items-center mb-4 bg-transparent border-0 p-0 text-secondary"
          type="button"
          onClick={() => navigate(-1)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
        </button>

        {/* Header */}
        <header className="mb-4 d-flex align-items-center gap-3">
          <span className="kc-header-icon d-inline-flex align-items-center justify-content-center">
            <Icon icon="mdi:cog" width="16" />
          </span>
          <div>
            <h3 className="kc-title mb-1">Knowledgebase customization</h3>
            <p className="kc-subtitle mb-0">
              Customize the appearance and branding of your knowledgebase.
            </p>
          </div>
        </header>

        {/* Custom Menu Link */}
        <section className="kc-card mb-3">
          <div className="kc-card-header">
            <h5 className="kc-card-title mb-0 d-flex align-items-center gap-2">
              <Icon icon="mdi:link" width="16" className="kc-card-icon-glyph" />
              Configure custom page for locations
            </h5>
          </div>
          <div className="kc-card-body">
            <div className="kc-field">
              <label className="kc-label">Custom menu link title</label>
              <input
                type="text"
                className="kc-input"
                placeholder="Enter your custom menu link title"
                value={formValues.customMenuLinkTitle}
                onChange={(e) => handleChange("customMenuLinkTitle", e.target.value)}
              />
            </div>
            <div className="kc-field mb-0">
              <label className="kc-label">Select locations to add custom menu link</label>
              <Select
                options={locations}
                isMulti
                value={formValues.customMenuLinkLocations}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    customMenuLinkLocations: e,
                  }))
                }
                classNamePrefix="kc-rs"
                styles={selectStyles}
              />
            </div>
          </div>
          <div className="kc-card-footer d-flex justify-content-end">
            <button
              type="button"
              className="kc-btn kc-btn--primary"
              onClick={configureCustomMenuLink}
            >
              <Icon icon="mdi:link" width="14" className="me-1" />
              {formValues?.customMenuLinkId ? "Update" : "Create"} custom menu link
            </button>
          </div>
        </section>

        <form>
          {/* Languages */}
          <section className="kc-card mb-3">
            <div className="kc-card-header">
              <h5 className="kc-card-title mb-0 d-flex align-items-center gap-2">
                <Icon icon="mdi:web" width="16" className="kc-card-icon-glyph" />
                Knowledgebase languages
              </h5>
            </div>
            <div className="kc-card-body">
              <div className="kc-field">
                <label className="kc-label">Default language</label>
                <Select
                  options={languages}
                  value={languages.find((item) => item.value == formValues.languageId)}
                  onChange={(e) => handleSelectChange("languageId", e)}
                  placeholder="Select a default language"
                  isSearchable
                  classNamePrefix="kc-rs"
                  styles={selectStyles}
                />
              </div>

              <label className="kc-checkbox mb-3">
                <input
                  type="checkbox"
                  id="allowMultipleLanguages"
                  checked={formValues.allowMultipleLanguages}
                  onChange={(e) =>
                    handleChange("allowMultipleLanguages", e.target.checked)
                  }
                />
                <span className="kc-checkbox-box" aria-hidden>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span>Allow multiple languages for sub accounts</span>
              </label>

              {formValues.allowMultipleLanguages && (
                <div className="kc-field mb-0">
                  <label className="kc-label">Select allowed languages</label>
                  <Select
                    options={languages}
                    isMulti
                    value={formValues.allowLanguages}
                    onChange={(e) => handleMultiSelectChange("allowLanguages", e)}
                    placeholder="Select languages your knowledgebase supports"
                    classNamePrefix="kc-rs"
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Hero customization */}
          <section className="kc-card mb-3">
            <div className="kc-card-header">
              <h5 className="kc-card-title mb-0 d-flex align-items-center gap-2">
                <Icon icon="mdi:palette" width="16" className="kc-card-icon-glyph" />
                Hero section
              </h5>
            </div>
            <div className="kc-card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <ColorPicker label="Background color" field="bgColor" />
                </div>
                <div className="col-md-6">
                  <ColorPicker label="Text color" field="textColor" />
                </div>
              </div>

              <div className="kc-preview-wrap">
                <label className="kc-label">Live preview</label>
                <div
                  className="kc-preview"
                  style={{
                    backgroundColor: formValues.bgColor,
                    color: formValues.textColor,
                  }}
                >
                  <h4 className="mb-2">
                    Welcome to {formValues.companyName || "Your Company"}
                  </h4>
                  <p className="mb-0">
                    This is how your hero section will appear to visitors
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Branding */}
          <section className="kc-card mb-3">
            <div className="kc-card-header">
              <h5 className="kc-card-title mb-0 d-flex align-items-center gap-2">
                <Icon icon="mdi:image-outline" width="16" className="kc-card-icon-glyph" />
                Branding
              </h5>
            </div>
            <div className="kc-card-body">
              <div className="kc-field">
                <label className="kc-label">Company logo</label>
                <div className="d-flex align-items-start gap-3 flex-wrap">
                  <div className="text-center">
                    <div className="kc-logo-preview d-flex align-items-center justify-content-center">
                      {formValues.logo ? (
                        <img
                          src={
                            formValues.logo instanceof File
                              ? URL.createObjectURL(formValues.logo)
                              : formValues.logo
                          }
                          alt="Logo preview"
                          className="img-fluid"
                          style={{ maxHeight: 76, maxWidth: 76 }}
                        />
                      ) : (
                        <Icon icon="mdi:image-outline" width="26" className="text-muted" />
                      )}
                    </div>
                    <small className="kc-helper">Preview</small>
                  </div>
                  <div className="flex-grow-1" style={{ minWidth: 240 }}>
                    <div className="kc-dropzone text-center">
                      <Icon icon="mdi:cloud-upload" width="28" className="kc-dropzone-icon" />
                      <div className="kc-dropzone-title">Upload your company logo</div>
                      <div className="kc-dropzone-meta">
                        PNG, JPG up to 2MB · Recommended: 200×200px
                      </div>
                      <input
                        type="file"
                        className="d-none"
                        id="logo-upload"
                        onChange={handleLogoChange}
                        accept="image/*"
                      />
                      <label htmlFor="logo-upload" className="kc-btn kc-btn--ghost mt-3">
                        Choose file
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="kc-field mb-0">
                <label className="kc-label">Company name</label>
                <input
                  type="text"
                  className="kc-input"
                  placeholder="Enter your company name"
                  value={formValues.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* White label domain */}
          <section className="kc-card mb-3">
            <div className="kc-card-header d-flex justify-content-between align-items-center">
              <h5 className="kc-card-title mb-0 d-flex align-items-center gap-2">
                <Icon icon="mdi:domain" width="16" className="kc-card-icon-glyph" />
                White label domain
              </h5>
              {formValues.domain && formValues.domain.trim() !== "" && (
                <button
                  type="button"
                  className="kc-btn kc-btn--ghost kc-btn--sm"
                  onClick={() => window.open(`https://${formValues.domain}`, "_blank")}
                >
                  <Icon icon="mdi:open-in-new" width="14" className="me-1" />
                  Preview
                </button>
              )}
            </div>
            <div className="kc-card-body">
              <label className="kc-label">Custom domain</label>
              <div className="kc-input-group position-relative">
                <span className="kc-input-prefix d-flex align-items-center justify-content-center">
                  <Icon icon="mdi:web" width="14" />
                </span>
                <input
                  type="text"
                  className="kc-input kc-input--with-prefix"
                  placeholder="support.yourdomain.com"
                  value={formValues.domain}
                  onChange={(e) => {
                    setDomainUpdate(true);
                    handleChange("domain", e.target.value);
                  }}
                />
              </div>
              <div className="kc-callout mt-3">
                <Icon icon="mdi:information-outline" width="14" className="kc-callout-icon" />
                <div>
                  <strong>Configuration required:</strong> Point your CNAME record to our servers.
                  Instructions will appear after saving changes.
                </div>
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="kc-btn kc-btn--primary"
              onClick={handleSubmit}
            >
              <Icon icon="mdi:content-save" width="14" className="me-1" />
              Save changes
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .kc-page {
          font-family: 'Inter', sans-serif;
          background: #fafafa;
          min-height: 100vh;
        }

        .kc-back-btn { transition: color 0.15s ease; }
        .kc-back-btn:hover { color: #1f2937 !important; }
        .kc-back-btn:hover svg { transform: translateX(-2px); }
        .kc-back-btn svg { transition: transform 0.15s ease; }

        .kc-header-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: #eef2ff;
          color: #4f46e5;
          flex-shrink: 0;
        }
        .kc-title {
          font-size: 1.0625rem;
          font-weight: 600;
          letter-spacing: -0.005em;
          color: #1f2937;
          line-height: 1.2;
        }
        .kc-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.5;
        }

        /* Card */
        .kc-card {
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 14px;
          overflow: hidden;
        }
        .kc-card-header {
          padding: 14px 18px;
          border-bottom: 1px solid #f1f1f1;
          background: #fafafa;
        }
        .kc-card-title {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: -0.005em;
        }
        .kc-card-icon-glyph {
          color: #4f46e5;
        }
        .kc-card-body { padding: 18px; }
        .kc-card-footer {
          padding: 12px 18px;
          border-top: 1px solid #f1f1f1;
        }

        /* Fields */
        .kc-field { margin-bottom: 16px; }
        .kc-label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 6px;
        }
        .kc-helper {
          display: block;
          font-size: 0.6875rem;
          color: #9ca3af;
          margin-top: 6px;
        }
        .kc-input {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0 12px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          font-family: inherit;
        }
        .kc-input::placeholder { color: #9ca3af; }
        .kc-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .kc-input--with-prefix { padding-left: 38px; }
        .kc-input-prefix {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          pointer-events: none;
        }

        /* Color picker */
        .kc-color-swatch {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          padding: 4px;
          background: #fff;
          flex-shrink: 0;
          cursor: pointer;
          transition: border-color 0.15s ease;
        }
        .kc-color-swatch:hover { border-color: #d1d5db; }
        .kc-color-input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
          width: 100%;
          height: 100%;
        }
        .kc-color-preview {
          display: block;
          width: 100%;
          height: 100%;
          border-radius: 5px;
          border: 1px solid rgba(0, 0, 0, 0.06);
        }

        /* Preview */
        .kc-preview-wrap {
          margin-top: 18px;
          padding-top: 18px;
          border-top: 1px solid #f1f1f1;
        }
        .kc-preview {
          border-radius: 10px;
          border: 1px dashed #d1d5db;
          padding: 36px 20px;
          text-align: center;
          min-height: 140px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        /* Checkbox */
        .kc-checkbox {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 0.875rem;
          color: #1f2937;
          user-select: none;
        }
        .kc-checkbox input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .kc-checkbox-box {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          border: 1px solid #d1d5db;
          background: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: transparent;
          transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
          flex-shrink: 0;
        }
        .kc-checkbox input:checked + .kc-checkbox-box {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          border-color: #4f46e5;
          color: #fff;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22);
        }

        /* Logo dropzone */
        .kc-logo-preview {
          width: 96px;
          height: 96px;
          border-radius: 12px;
          background: #fafafa;
          border: 1px solid #ececec;
        }
        .kc-dropzone {
          border: 1.5px dashed #d1d5db;
          border-radius: 12px;
          background: #fafafa;
          padding: 22px 18px;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .kc-dropzone:hover {
          border-color: #4f46e5;
          background: #f5f3ff;
        }
        .kc-dropzone-icon {
          color: #9ca3af;
          margin-bottom: 6px;
        }
        .kc-dropzone-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1f2937;
        }
        .kc-dropzone-meta {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-top: 2px;
        }

        /* Callout */
        .kc-callout {
          display: flex;
          gap: 8px;
          padding: 12px 14px;
          background: #f5f3ff;
          border: 1px solid #e0e7ff;
          border-radius: 10px;
          font-size: 0.8125rem;
          color: #4338ca;
          line-height: 1.5;
        }
        .kc-callout-icon {
          color: #4f46e5;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .kc-callout strong {
          color: #312e81;
          font-weight: 600;
        }

        /* Buttons */
        .kc-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 9px 16px;
          border-radius: 9px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          text-decoration: none;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .kc-btn--sm { padding: 6px 12px; font-size: 0.75rem; }
        .kc-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .kc-btn--ghost {
          background: #fff;
          border-color: #e5e7eb;
          color: #1f2937;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
        }
        .kc-btn--ghost:hover {
          background: #fafafa;
          border-color: #d1d5db;
          color: #1f2937;
          transform: translateY(-1px);
          text-decoration: none;
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
        }
        .kc-btn--ghost:active { transform: translateY(0); }
        .kc-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .kc-btn--primary:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          color: #fff;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .kc-btn--primary:active { transform: translateY(0); }
      `}</style>
    </div>
  );
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderColor: state.isFocused ? "#4f46e5" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(79, 70, 229, 0.1)" : "none",
    borderRadius: 8,
    "&:hover": { borderColor: state.isFocused ? "#4f46e5" : "#d1d5db" },
    fontSize: "0.875rem",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
      ? "#f5f3ff"
      : "#fff",
    color: state.isSelected ? "#fff" : "#1f2937",
    fontSize: "0.875rem",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(17, 24, 39, 0.08)",
    border: "1px solid #ececec",
  }),
  multiValue: (base) => ({
    ...base,
    background: "#eef2ff",
    border: "1px solid #e0e7ff",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#4338ca",
    fontWeight: 500,
    fontSize: "0.75rem",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#4f46e5",
    ":hover": { background: "#4f46e5", color: "#fff" },
  }),
};

export default KnowledgebaseCustomization;
